





















































import {
  defineComponent,
  ref,
  computed,
  watch,
  reactive
} from "@vue/composition-api";
import { Chart } from "chart.js";
import ChartCard from "@/components/Atoms/ChartCard/index.vue";
import Heading from "@/components/Atoms/Typography/Heading/index.vue";
import EntryPvTable from "../EntryPvTable/index.vue";
import { percentageOneDecimal } from "@/helpers/format-helper";
import {
  chartBlue,
  chartBlueLight,
  chartBlueDeep,
  chartGray01,
  chartTransparent
} from "@/assets/styles/vars";

export default defineComponent({
  components: {
    Heading,
    EntryPvTable,
    ChartCard
  },
  props: {
    data: {
      type: Array,
      default: () => [{ date: "", totalEntry: 0, totalPv: 0 }]
    },
    categoryData: {
      type: Array,
      default: () => [{ date: "", totalEntry: 0, totalPv: 0 }]
    },
    period: {
      type: String,
      default: ""
    },
    category: {
      type: String,
      default: "0"
    },
    categoryName: {
      type: String,
      default: ""
    },
    totalEntry: {
      type: Number,
      default: 0
    },
    totalPv: {
      type: Number,
      default: 0
    }
  },
  name: "EntryPvChart",
  setup(props) {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const state = reactive<{ chart: Chart | null }>({
      chart: null
    });

    const displayPeriod = computed(() => props.period);
    const displayCategory = computed(() => props.category === "1");
    const businessCategory = computed(() => props.categoryName);
    const tableData = computed(() => props.data);
    const tableCategoryData = computed(() => props.categoryData);

    const pvLabels = computed(() =>
      props.data.map(e => {
        const el = e as { date: string; totalPv: number; totalEntry: number };
        return el.date;
      })
    );
    const pvData = computed(() =>
      props.data.map(e => {
        const el = e as { date: string; totalPv: number; totalEntry: number };
        return el.totalPv;
      })
    );
    const entryData = computed(() =>
      props.data.map(e => {
        const el = e as { date: string; totalPv: number; totalEntry: number };
        return el.totalEntry;
      })
    );
    const entryRate = computed(() =>
      props.data.map(e => {
        const el = e as { date: string; totalPv: number; totalEntry: number };
        return percentageOneDecimal(el.totalEntry, el.totalPv);
      })
    );

    const categoryRate = computed(() => {
      if (props.category === "0") {
        return [];
      }
      const res = props.data.map(e => {
        const pvEntryData = e as {
          date: string;
          totalPv: number;
          totalEntry: number;
        };
        const matchedItem = props.categoryData.find(c => {
          const category = c as {
            date: string;
            totalPv: number;
            totalEntry: number;
          };
          return category.date === pvEntryData.date;
        });
        return matchedItem
          ? matchedItem
          : { date: pvEntryData.date, totalPv: 0, totalEntry: 0 };
      });
      return res.map(i => {
        const item = i as {
          date: string;
          totalPv: number;
          totalEntry: number;
        };
        return item.totalPv === 0
          ? 0
          : percentageOneDecimal(item.totalEntry, item.totalPv);
      });
    });

    watch(pvData, () => {
      if (pvData) {
        createCharts(
          pvLabels.value as Array<string>,
          pvData.value as Array<number>,
          entryData.value as Array<number>,
          entryRate.value as Array<number>,
          categoryRate.value as Array<number>,
          businessCategory.value as string
        );
      }
    });

    // 業界平均表示の切り替え
    watch(categoryRate, () => {
      if (categoryRate) {
        createCharts(
          pvLabels.value as Array<string>,
          pvData.value as Array<number>,
          entryData.value as Array<number>,
          entryRate.value as Array<number>,
          categoryRate.value as Array<number>,
          businessCategory.value as string
        );
      }
    });

    function createCharts(
      labels: Array<string>,
      pv: Array<number>,
      entry: Array<number>,
      entryRate: Array<number>,
      categoryRate: Array<number>,
      businessCategory: string
    ) {
      if (canvasRef.value === null) return;
      const canvas = canvasRef.value.getContext("2d");
      if (canvas === null) return;

      const baseBarCount = 30;
      const pointRadiusSize =
        pv.length <= baseBarCount ? 3 : pv.length > baseBarCount * 2 ? 1 : 2;
      const borderWidthSize = pv.length > baseBarCount * 2 ? 1 : 2;

      const chartData = {
        labels: labels,
        datasets: [
          {
            label: "エントリー率",
            data: entryRate,
            type: "line",
            borderColor: chartBlueDeep,
            pointBackgroundColor: chartBlueDeep,
            backgroundColor: chartTransparent,
            borderWidth: borderWidthSize,
            pointRadius: pointRadiusSize,
            lineTension: 0,
            order: 0,
            yAxisID: "y2"
          },
          {
            label: `${businessCategory}業界平均エントリー率`,
            type: "line",
            data: categoryRate,
            borderColor: chartGray01,
            pointBackgroundColor: chartGray01,
            backgroundColor: chartTransparent,
            borderWidth: borderWidthSize,
            pointRadius: pointRadiusSize,
            lineTension: 0,
            order: 1,
            yAxisID: "y2"
          },
          {
            label: "エントリー数",
            data: entry,
            backgroundColor: chartBlue,
            order: 2,
            maxBarThickness: 80,
            minBarLength: 1,
            yAxisID: "y1"
          },
          {
            label: "PV数",
            data: pv,
            backgroundColor: chartBlueLight,
            order: 3,
            maxBarThickness: 80,
            minBarLength: 1,
            yAxisID: "y1"
          }
        ]
      };

      if (state && state.chart !== null) {
        state.chart.data.datasets = chartData.datasets;
        state.chart.data.labels = chartData.labels;
        state.chart.update();
      } else {
        const chart = new Chart(canvas, {
          type: "bar",
          options: {
            scales: {
              xAxes: [
                {
                  stacked: true,
                  gridLines: {
                    display: false
                  },
                  ticks: {
                    stepSize: 30
                  }
                }
              ],
              yAxes: [
                {
                  id: "y1",
                  type: "linear",
                  position: "left",
                  gridLines: {
                    display: false
                  },
                  ticks: {
                    min: 0,
                    callback: function(value) {
                      if (Math.floor(value as number) === value) {
                        return value;
                      }
                    }
                  }
                },
                {
                  id: "y2",
                  type: "linear",
                  position: "right",
                  gridLines: {
                    display: true,
                    borderDash: [9]
                  },
                  ticks: {
                    min: 0,
                    callback: value => value + "%"
                  }
                }
              ]
            },
            legend: {
              display: false
            },
            tooltips: {
              callbacks: {
                label: (tip, data): string | string[] => {
                  if (
                    data.datasets === undefined ||
                    tip.datasetIndex === undefined
                  )
                    return "";

                  const label = `${data.datasets[tip.datasetIndex].label}: ${
                    tip.yLabel
                  }`;
                  if (tip.datasetIndex <= 1) {
                    return label + "%";
                  }
                  return label;
                }
              }
            }
          }
        });

        state.chart = chart;
        state.chart.data.datasets = chartData.datasets;
        state.chart.data.labels = chartData.labels;
        state.chart.update();
      }
    }

    return {
      canvasRef,
      pvLabels,
      pvData,
      entryData,
      entryRate,
      categoryRate,
      state,
      tableData,
      tableCategoryData,
      displayPeriod,
      displayCategory,
      businessCategory,
      createCharts
    };
  }
});
